import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';

import { Icon, Text, H4, Tooltip } from '../../../../../components';

// Loading spinner animation
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Loader entrance animation
const loaderEntrance = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
  border-radius: 12px;
  margin-top: 12px;
  background: var(--elevation-low);
  animation: ${loaderEntrance} 0.3s ease-out;
  animation-fill-mode: both;
`;

const LoaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const LoaderIndicator = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 10px;
  background-color: ${(props) => {
    if (props.completed) return 'var(--primary-default)';
    return 'transparent';
  }};
  color: ${(props) => {
    if (props.completed) return 'white';
    return 'var(--border-muted)';
  }};
  border: none;
  transition: all 0.3s ease;
  flex-shrink: 0;
`;

const SpinningIcon = styled(Icon)`
  animation: ${spin} 1.5s linear infinite;
`;

const LoaderTextContent = styled.div`
  flex: 1;
  padding-top: 0;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const BusinessLogicLoader = ({ isLoading, translatedTerm }) => {
  const getLoaderIndicatorContent = () => {
    if (isLoading) {
      return <SpinningIcon name='steps-loader' size={16} />;
    }
    if (translatedTerm) {
      return <Icon name='step-checkbox' size={16} />;
    }
    return null;
  };

  // Don't render if not loading and no completed term
  if (!isLoading && !translatedTerm) {
    return null;
  }

  return (
    <LoaderContainer>
      <LoaderContent>
        <LoaderIndicator completed={!!translatedTerm && !isLoading}>
          {getLoaderIndicatorContent()}
        </LoaderIndicator>
        <LoaderTextContent>
          <TitleContainer>
            <H4>Supper translation</H4>
            <Tooltip
              content="Describe the logic you'd like Supper to understand and apply"
              position='top'
            >
              <Icon name='information' color='var(--content-subtitle)' displaySize={16} />
            </Tooltip>
          </TitleContainer>
        </LoaderTextContent>
      </LoaderContent>
      <Text>{translatedTerm?.translation}</Text>
    </LoaderContainer>
  );
};

BusinessLogicLoader.propTypes = {
  isLoading: PropTypes.bool,
  translatedTerm: PropTypes.shape({
    translation: PropTypes.string,
    category: PropTypes.string
  })
};

BusinessLogicLoader.defaultProps = {
  isLoading: false
};

export { BusinessLogicLoader };
